import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function AttendanceImportPage() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/attendance/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.imported} rows, skipped ${data.skipped_count}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Import attendance</h1>
        <p className="mt-1 text-sm text-zinc-500">Bulk-load historical daily task logs from a CSV. Rows are marked as regularized so they won't be auto-flagged absent.</p>
      </div>

      <Card className="border-zinc-200 shadow-none">
        <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /> CSV format</CardTitle></CardHeader>
        <CardContent className="text-sm text-zinc-600 space-y-2">
          <div>Header row must include (case-insensitive):</div>
          <pre className="text-xs bg-zinc-50 border border-zinc-200 rounded p-3 overflow-x-auto">employee_email <span className="text-zinc-400">or</span> employee_code, date, log_in, task_given, log_out, task_completed, remarks</pre>
          <div className="text-xs text-zinc-500">Dates like <code>29th July' 2025</code>, <code>29/07/2025</code>, <code>2025-07-29</code> are all accepted. Times can be <code>09:15</code> or <code>9:15 AM</code>.</div>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-6 space-y-4">
          <input ref={ref} type="file" accept=".csv,text/csv" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
          <div className="flex flex-wrap gap-3 items-center">
            <Button type="button" variant="outline" className="border-zinc-300" onClick={() => ref.current?.click()} data-testid="import-choose-btn">
              <Upload className="h-4 w-4 mr-2" /> Choose CSV
            </Button>
            <span className="text-sm text-zinc-500">{file ? file.name : "No file selected"}</span>
            <Button onClick={submit} disabled={!file || busy} className="bg-emerald-600 hover:bg-emerald-700 ml-auto" data-testid="import-submit-btn">
              {busy ? "Importing…" : "Import"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-zinc-200 shadow-none">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-medium">Imported <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{result.imported}</Badge></span>
              <span className="text-sm font-medium">Skipped <Badge className="bg-amber-50 text-amber-700 border-amber-200">{result.skipped_count}</Badge></span>
            </div>
            {result.skipped?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3">
                <div className="text-xs font-medium text-amber-800 flex items-center gap-1 mb-2"><AlertTriangle className="h-3 w-3" /> Skipped rows (first 50)</div>
                <ul className="text-xs text-amber-900 space-y-1 max-h-64 overflow-y-auto font-mono">
                  {result.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
